import axios from 'axios'
import { isEmpty } from 'lodash'
import cacheData from "memory-cache";

import { resolveRequest, TRequestResult } from 'utility-tools/module/requests'
import { getPairQuery, TGetVolumesApiDto, TPriceWindowApiDto } from 'nexchange-service/module/coins'
import { ECoinInputType } from 'nexchange-service/module/exchangeWidget'
import constants from 'nexchange-service/constants'
import { getPaymentMethod } from 'nexchange-service/module/payment'

import {
    TPriceRespApiDto,
    TPairApiDto,
    TCurrencyApiDto,
    TGetPairPriceApiProps,
    TBulkPairApiDto,
} from './api.types'
import { appConfigContext } from '../../../appServiceConfig'
import { EPaymentMethodId } from '../../order/utils/paymentMethod'

const { API_URL  } = constants

const getCurrentPaymentMethod = () => {
    const { DEFAULT_PAYMENT_METHOD } = appConfigContext.getAppConfig()
    const paymentMethodFromLocalStorage = getPaymentMethod()
    const paymentMethod = paymentMethodFromLocalStorage && Object.values(EPaymentMethodId).includes(paymentMethodFromLocalStorage) ? paymentMethodFromLocalStorage : null
    return paymentMethod || DEFAULT_PAYMENT_METHOD
}

const fetchWithCache = async <T>(url, minutes = 1): Promise<{ data: T; error: any }> => {
    const value = cacheData.get(url);
    if (value) {
        return { data: value, error: null };
    } else {
        const { data, error } = await resolveRequest<T>(
            axios.get(url),
        )
        cacheData.put(url, data, minutes * 1000 * 60);
        return { data, error };
    }
}


// TODO: add ts and wrap with promise handler
const getPairPrice = async ({
    coinCodeFrom = '',
    coinCodeTo = '',
    pair = null,
    amountFrom = 0,
    amountTo = 0,
}: TGetPairPriceApiProps = {}): Promise<TPriceRespApiDto | null> => {
    const coinsPairQuery = getPairQuery({
        pair,
        coinCodeFrom,
        coinCodeTo,
    })
    const paymentMethod = getCurrentPaymentMethod()

    const quoteAmountParam = amountFrom !== undefined && amountFrom !== null ? `amount_quote=${Number(amountFrom)}` : ''
    const baseAmountParam = amountTo !== undefined && amountTo !== null? `amount_base=${Number(amountTo)}` : ''


    const amountQueryParam = `${quoteAmountParam || baseAmountParam ? '?' : ''}${quoteAmountParam}${quoteAmountParam && baseAmountParam ? '&' : ''}${baseAmountParam}
        ${paymentMethod ? `&payment_method=${paymentMethod}` : ''}` // TODO: IMPROVE THIS LOGIC

    // Backend returns 400 on success
    // return resolveRequest(
    //   axios.get(`${API_URL}/get_price/${coinsPair}${amountQueryParam}`),
    //   { ignoreFailureCodes: [400] }
    // );

    return axios
        .get(`${API_URL}/get_price/${coinsPairQuery}/${amountQueryParam}`)
        .then(res => res.data)
        .catch(err => {
            const { response } = err
            if (!response) return err

            const { status, data } = response

            if (status === 400 && !isEmpty(data)) {
                return data
            }
            return null
        })
}

const requestGetAvailableCurrencies = async (): Promise<TRequestResult<TCurrencyApiDto[]>> => {
    const { data, error } = await resolveRequest<TCurrencyApiDto[]>(
        axios.get(`${API_URL}/currency/`), 3
    )

    if (!error) {
        const availableCurrencies = data.filter(currency => currency.has_enabled_pairs)
        return { data: availableCurrencies ?? [], error }
    }

    return { data, error }
}

const requestGetAvailablePairs = async (fiatOnly = false, availablePairsList = ''): Promise<TRequestResult<TBulkPairApiDto[]>> => {
    let queryParams = ''
    const query = availablePairsList ? `?pairs=${availablePairsList}` : ''
    if (fiatOnly)
        queryParams += '?fiat_only=true'
    const { data, error } = await fetchWithCache<TBulkPairApiDto[]>(`${API_URL}/info/bulk/${query}`, 60)
    if (!error) {
        // Some of the pairs turned off by setting MIN > MAX in the backend
        // const availablePairs = data.filter(
        //     ({ max_receive_amount, min_receive_amount, max_deposit_amount, min_deposit_amount }) =>
        //         min_receive_amount < max_receive_amount && min_deposit_amount < max_deposit_amount,
        // )

        return { data: data ?? [], error }
    }

    return { data, error }
}

const requestGetVolumes = async (
    timespanInHour?: number,
): Promise<TRequestResult<TGetVolumesApiDto>> =>
    // https://api.n.exchange/en/api/v1/volume/
    resolveRequest(axios.get(`${API_URL}/volume/?hours=${timespanInHour ?? 24}`))

const requestGetAllPairs = async (): Promise<TRequestResult<TPairApiDto[]>> => {
    return resolveRequest<TPairApiDto[]>(axios.get(`${API_URL}/pair/`))
}

type TRequestGetPairRatesProps = {
    amount?: number | string | null
    amountOf?: ECoinInputType | null
} & ({ pairName: string } | { fromCode: string; toCode: string })

const requestGetPairRates = async ({
    // @ts-ignore
    pairName,
    // @ts-ignore
    fromCode,
    // @ts-ignore
    toCode,
    amount,
    amountOf,
}: TRequestGetPairRatesProps): Promise<TRequestResult<TPriceRespApiDto>> => {
    const paymentMethod = getCurrentPaymentMethod()
    const pairQuery = pairName ?? `${toCode}${fromCode}`
    const amountInputType = amountOf ?? ECoinInputType.FROM
    const amountQuery = amount
        ? `amount_${amountInputType === ECoinInputType.FROM ? 'quote' : 'base'}=${amount}`
        : ''

    return resolveRequest<TPriceRespApiDto>(
        axios.get(`${API_URL}/get_price/${pairQuery}/?${amountQuery}
        ${paymentMethod ? `&payment_method=${paymentMethod}` : ''}`),
    )
}

export type { TRequestGetPairRatesProps }
export {
    getPairPrice,
    requestGetAvailableCurrencies,
    requestGetAvailablePairs,
    requestGetAllPairs,
    requestGetPairRates,
    requestGetVolumes,
    fetchWithCache
}
